import React, { useState } from 'react';
import { FaFilePdf, FaFileExcel, FaFileCode, FaCalendarAlt, FaClock, FaEnvelope, FaDownload, FaTimes, FaCheck, FaSpinner, FaPlus } from 'react-icons/fa';
import api from '../config/api';
import './ReportBuilder.css';

const DASHBOARD_SECTIONS = [
  { id: 'keyMetrics', label: 'Key Metrics Overview', description: 'Reports count, cleanup rate, response time, pending items' },
  { id: 'priorityBreakdown', label: 'Priority Breakdown', description: 'Visual breakdown of Critical, High, Medium, Low priorities' },
  { id: 'severityScoring', label: 'Severity Scoring System', description: 'Explanation of the waste severity scoring methodology' },
  { id: 'priorityQueue', label: 'Priority Queue', description: 'Current pending reports and their severity scores' },
  { id: 'timeByCategory', label: 'Response Time by Category', description: 'Average cleanup time for each waste category' },
  { id: 'slaCompliance', label: 'SLA Compliance', description: 'Performance against service level agreements' },
  { id: 'responseTrends', label: 'Response Trends', description: 'Weekly report volume and trend analysis' },
  { id: 'contributors', label: 'Top Contributors', description: 'Most active community reporters in your territory' }
];

const SUBSCRIBER_SECTIONS = {
  authority: [
    { id: 'crewManagement', label: 'Crew Management', description: 'Overview of cleanup crews and capacity' }
  ],
  private_company: [
    { id: 'jobPerformance', label: 'Job Performance', description: 'Completed jobs, revenue, and efficiency metrics' }
  ],
  recycling: [
    { id: 'materialInventory', label: 'Material Inventory', description: 'Collected materials and estimated values' }
  ]
};

const WASTE_CATEGORIES = [
  'Household', 'Commercial', 'Construction', 'Electronic', 'Automobile', 'Hospital', 'Industrial', 'Agricultural', 'Environmental'
];

const ReportBuilder = ({ isOpen, onClose, subscriberType }) => {
  const [activeTab, setActiveTab] = useState('custom');
  const [selectedSections, setSelectedSections] = useState(['keyMetrics', 'priorityBreakdown']);
  const [dateRange, setDateRange] = useState({
    start: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    end: new Date().toISOString().split('T')[0]
  });
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [responseTimeRange, setResponseTimeRange] = useState({ min: 0, max: 30 });
  const [exportFormat, setExportFormat] = useState('pdf');
  const [loading, setLoading] = useState(false);
  const [scheduledReports, setScheduledReports] = useState([]);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [scheduleForm, setScheduleForm] = useState({
    frequency: 'weekly',
    emails: '',
    dateRangeType: 'last_month'
  });

  const allSections = [
    ...DASHBOARD_SECTIONS,
    ...(SUBSCRIBER_SECTIONS[subscriberType] || [])
  ];

  const toggleSection = (sectionId) => {
    setSelectedSections(prev =>
      prev.includes(sectionId)
        ? prev.filter(id => id !== sectionId)
        : [...prev, sectionId]
    );
  };

  const toggleCategory = (category) => {
    setSelectedCategories(prev =>
      prev.includes(category)
        ? prev.filter(c => c !== category)
        : [...prev, category]
    );
  };

  const selectAllSections = () => {
    setSelectedSections(allSections.map(s => s.id));
  };

  const clearAllSections = () => {
    setSelectedSections([]);
  };

  const handleExportExecutive = async () => {
    setLoading(true);
    try {
      const response = await api.post('/api/reports/executive', { dateRange }, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `executive_report_${Date.now()}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Error generating executive report:', error);
      alert('Failed to generate executive report');
    } finally {
      setLoading(false);
    }
  };

  const handleExportCustom = async () => {
    if (selectedSections.length === 0) {
      alert('Please select at least one section to include in the report');
      return;
    }

    setLoading(true);
    try {
      const options = {
        categories: selectedCategories.length > 0 ? selectedCategories : undefined,
        responseTimeRange
      };

      if (exportFormat === 'pdf') {
        const response = await api.post('/api/reports/custom/pdf', {
          dateRange,
          selectedSections,
          options
        }, { responseType: 'blob' });
        
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `custom_report_${Date.now()}.pdf`);
        document.body.appendChild(link);
        link.click();
        link.remove();
      } else if (exportFormat === 'html') {
        const response = await api.post('/api/reports/custom/html', {
          dateRange,
          selectedSections,
          options
        });
        
        const blob = new Blob([response.data], { type: 'text/html' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `custom_report_${Date.now()}.html`);
        document.body.appendChild(link);
        link.click();
        link.remove();
      }
    } catch (error) {
      console.error('Error generating custom report:', error);
      alert('Failed to generate custom report');
    } finally {
      setLoading(false);
    }
  };

  const handleExportCSV = async () => {
    setLoading(true);
    try {
      const response = await api.post('/api/reports/csv', {
        dateRange,
        categories: selectedCategories.length > 0 ? selectedCategories : undefined
      }, { responseType: 'blob' });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `waste_reports_${Date.now()}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Error generating CSV:', error);
      alert('Failed to generate CSV export');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateSchedule = async () => {
    try {
      const emails = scheduleForm.emails.split(',').map(e => e.trim()).filter(e => e);
      if (emails.length === 0) {
        alert('Please enter at least one email address');
        return;
      }

      await api.post('/api/reports/scheduled', {
        reportType: 'executive',
        frequency: scheduleForm.frequency,
        emailRecipients: emails,
        selectedSections,
        dateRangeType: scheduleForm.dateRangeType,
        categories: selectedCategories
      });

      alert('Scheduled report created successfully');
      setShowScheduleModal(false);
      loadScheduledReports();
    } catch (error) {
      console.error('Error creating scheduled report:', error);
      alert('Failed to create scheduled report');
    }
  };

  const loadScheduledReports = async () => {
    try {
      const response = await api.get('/api/reports/scheduled');
      setScheduledReports(response.data);
    } catch (error) {
      console.error('Error loading scheduled reports:', error);
    }
  };

  const handleDeleteSchedule = async (id) => {
    try {
      await api.delete(`/api/reports/scheduled/${id}`);
      loadScheduledReports();
    } catch (error) {
      console.error('Error deleting scheduled report:', error);
    }
  };

  React.useEffect(() => {
    if (isOpen && activeTab === 'scheduled') {
      loadScheduledReports();
    }
  }, [isOpen, activeTab]);

  if (!isOpen) return null;

  return (
    <div className="report-builder-overlay" onClick={onClose}>
      <div className="report-builder-modal" onClick={e => e.stopPropagation()}>
        <div className="report-builder-header">
          <h2><FaFilePdf /> Export & Reporting</h2>
          <button className="close-btn" onClick={onClose}><FaTimes /></button>
        </div>

        <div className="report-tabs">
          <button 
            className={activeTab === 'executive' ? 'active' : ''} 
            onClick={() => setActiveTab('executive')}
          >
            <FaFilePdf /> Executive Report
          </button>
          <button 
            className={activeTab === 'custom' ? 'active' : ''} 
            onClick={() => setActiveTab('custom')}
          >
            <FaFileCode /> Custom Report
          </button>
          <button 
            className={activeTab === 'csv' ? 'active' : ''} 
            onClick={() => setActiveTab('csv')}
          >
            <FaFileExcel /> CSV Export
          </button>
          <button 
            className={activeTab === 'scheduled' ? 'active' : ''} 
            onClick={() => setActiveTab('scheduled')}
          >
            <FaCalendarAlt /> Scheduled Reports
          </button>
        </div>

        <div className="report-builder-content">
          {activeTab === 'executive' && (
            <div className="executive-tab">
              <div className="tab-description">
                <h3>Monthly Executive Summary</h3>
                <p>Generate a comprehensive PDF report with graphs, charts, and detailed analysis (500+ words). 
                   This report includes all key metrics, priority breakdowns, SLA compliance, trends, and strategic insights.</p>
              </div>

              <div className="date-range-section">
                <h4><FaCalendarAlt /> Report Period</h4>
                <div className="date-inputs">
                  <div className="date-field">
                    <label>From</label>
                    <input 
                      type="date" 
                      value={dateRange.start}
                      onChange={e => setDateRange({...dateRange, start: e.target.value})}
                    />
                  </div>
                  <div className="date-field">
                    <label>To</label>
                    <input 
                      type="date" 
                      value={dateRange.end}
                      onChange={e => setDateRange({...dateRange, end: e.target.value})}
                    />
                  </div>
                </div>
              </div>

              <button 
                className="generate-btn executive"
                onClick={handleExportExecutive}
                disabled={loading}
              >
                {loading ? <><FaSpinner className="spin" /> Generating...</> : <><FaDownload /> Download Executive PDF</>}
              </button>
            </div>
          )}

          {activeTab === 'custom' && (
            <div className="custom-tab">
              <div className="tab-description">
                <h3>Custom Report Builder</h3>
                <p>Select specific sections to include in your report. Each section includes 100-150 words of detailed analysis.</p>
              </div>

              <div className="sections-header">
                <h4>Select Dashboard Sections</h4>
                <div className="section-actions">
                  <button onClick={selectAllSections}>Select All</button>
                  <button onClick={clearAllSections}>Clear All</button>
                </div>
              </div>

              <div className="sections-grid">
                {allSections.map(section => (
                  <div 
                    key={section.id}
                    className={`section-card ${selectedSections.includes(section.id) ? 'selected' : ''}`}
                    onClick={() => toggleSection(section.id)}
                  >
                    <div className="section-checkbox">
                      {selectedSections.includes(section.id) ? <FaCheck /> : null}
                    </div>
                    <div className="section-info">
                      <h5>{section.label}</h5>
                      <p>{section.description}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="filters-section">
                <div className="filter-group">
                  <h4><FaCalendarAlt /> Date Range</h4>
                  <div className="date-inputs">
                    <div className="date-field">
                      <label>From</label>
                      <input 
                        type="date" 
                        value={dateRange.start}
                        onChange={e => setDateRange({...dateRange, start: e.target.value})}
                      />
                    </div>
                    <div className="date-field">
                      <label>To</label>
                      <input 
                        type="date" 
                        value={dateRange.end}
                        onChange={e => setDateRange({...dateRange, end: e.target.value})}
                      />
                    </div>
                  </div>
                </div>

                <div className="filter-group">
                  <h4>Waste Categories (Optional)</h4>
                  <div className="category-chips">
                    {WASTE_CATEGORIES.map(cat => (
                      <span 
                        key={cat}
                        className={`category-chip ${selectedCategories.includes(cat) ? 'selected' : ''}`}
                        onClick={() => toggleCategory(cat)}
                      >
                        {cat}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="filter-group">
                  <h4><FaClock /> Response Time Filter (Days)</h4>
                  <div className="range-inputs">
                    <div className="range-field">
                      <label>Min</label>
                      <input 
                        type="number" 
                        min="0"
                        value={responseTimeRange.min}
                        onChange={e => setResponseTimeRange({...responseTimeRange, min: parseInt(e.target.value) || 0})}
                      />
                    </div>
                    <span>to</span>
                    <div className="range-field">
                      <label>Max</label>
                      <input 
                        type="number" 
                        min="0"
                        value={responseTimeRange.max}
                        onChange={e => setResponseTimeRange({...responseTimeRange, max: parseInt(e.target.value) || 30})}
                      />
                    </div>
                  </div>
                </div>

                <div className="filter-group">
                  <h4>Export Format</h4>
                  <div className="format-options">
                    <label className={exportFormat === 'pdf' ? 'selected' : ''}>
                      <input 
                        type="radio" 
                        name="format" 
                        value="pdf" 
                        checked={exportFormat === 'pdf'}
                        onChange={() => setExportFormat('pdf')}
                      />
                      <FaFilePdf /> PDF
                    </label>
                    <label className={exportFormat === 'html' ? 'selected' : ''}>
                      <input 
                        type="radio" 
                        name="format" 
                        value="html" 
                        checked={exportFormat === 'html'}
                        onChange={() => setExportFormat('html')}
                      />
                      <FaFileCode /> HTML
                    </label>
                  </div>
                </div>
              </div>

              <div className="selected-summary">
                <span>{selectedSections.length} sections selected</span>
                <span>Est. {selectedSections.length * 120} words analysis</span>
              </div>

              <button 
                className="generate-btn custom"
                onClick={handleExportCustom}
                disabled={loading || selectedSections.length === 0}
              >
                {loading ? <><FaSpinner className="spin" /> Generating...</> : <><FaDownload /> Generate Custom Report</>}
              </button>
            </div>
          )}

          {activeTab === 'csv' && (
            <div className="csv-tab">
              <div className="tab-description">
                <h3>CSV Data Export</h3>
                <p>Export raw waste report data for external analysis in spreadsheet software or data tools.</p>
              </div>

              <div className="csv-fields">
                <h4>Included Fields</h4>
                <div className="field-list">
                  <span>Report ID</span>
                  <span>Category</span>
                  <span>Subcategory</span>
                  <span>Status</span>
                  <span>Priority</span>
                  <span>Severity Score</span>
                  <span>Location</span>
                  <span>Coordinates</span>
                  <span>Reporter</span>
                  <span>Reported Date</span>
                  <span>Cleanup Date</span>
                  <span>Response Time</span>
                  <span>Description</span>
                </div>
              </div>

              <div className="filters-section">
                <div className="filter-group">
                  <h4><FaCalendarAlt /> Date Range</h4>
                  <div className="date-inputs">
                    <div className="date-field">
                      <label>From</label>
                      <input 
                        type="date" 
                        value={dateRange.start}
                        onChange={e => setDateRange({...dateRange, start: e.target.value})}
                      />
                    </div>
                    <div className="date-field">
                      <label>To</label>
                      <input 
                        type="date" 
                        value={dateRange.end}
                        onChange={e => setDateRange({...dateRange, end: e.target.value})}
                      />
                    </div>
                  </div>
                </div>

                <div className="filter-group">
                  <h4>Filter by Categories (Optional)</h4>
                  <div className="category-chips">
                    {WASTE_CATEGORIES.map(cat => (
                      <span 
                        key={cat}
                        className={`category-chip ${selectedCategories.includes(cat) ? 'selected' : ''}`}
                        onClick={() => toggleCategory(cat)}
                      >
                        {cat}
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              <button 
                className="generate-btn csv"
                onClick={handleExportCSV}
                disabled={loading}
              >
                {loading ? <><FaSpinner className="spin" /> Exporting...</> : <><FaFileExcel /> Download CSV</>}
              </button>
            </div>
          )}

          {activeTab === 'scheduled' && (
            <div className="scheduled-tab">
              <div className="tab-description">
                <h3>Scheduled Reports</h3>
                <p>Set up automatic email delivery of reports on a weekly or monthly schedule.</p>
              </div>

              <button 
                className="add-schedule-btn"
                onClick={() => setShowScheduleModal(true)}
              >
                <FaPlus /> Create New Schedule
              </button>

              {scheduledReports.length === 0 ? (
                <div className="no-schedules">
                  <FaCalendarAlt />
                  <p>No scheduled reports yet</p>
                  <span>Create a schedule to receive automatic reports via email</span>
                </div>
              ) : (
                <div className="schedules-list">
                  {scheduledReports.map(schedule => (
                    <div key={schedule.id} className="schedule-item">
                      <div className="schedule-info">
                        <h5>{schedule.report_type === 'executive' ? 'Executive Report' : 'Custom Report'}</h5>
                        <span className="frequency">{schedule.frequency}</span>
                        <p>Recipients: {schedule.email_recipients?.join(', ')}</p>
                        <p className="next-run">Next run: {schedule.next_run_at ? new Date(schedule.next_run_at).toLocaleDateString() : 'Not scheduled'}</p>
                      </div>
                      <div className="schedule-actions">
                        <span className={`status ${schedule.is_active ? 'active' : 'inactive'}`}>
                          {schedule.is_active ? 'Active' : 'Paused'}
                        </span>
                        <button onClick={() => handleDeleteSchedule(schedule.id)} className="delete-btn">
                          <FaTimes />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {showScheduleModal && (
                <div className="schedule-modal-overlay" onClick={() => setShowScheduleModal(false)}>
                  <div className="schedule-modal" onClick={e => e.stopPropagation()}>
                    <h3>Create Scheduled Report</h3>
                    
                    <div className="form-group">
                      <label>Frequency</label>
                      <select 
                        value={scheduleForm.frequency}
                        onChange={e => setScheduleForm({...scheduleForm, frequency: e.target.value})}
                      >
                        <option value="weekly">Weekly (Every Monday)</option>
                        <option value="monthly">Monthly (1st of Month)</option>
                      </select>
                    </div>

                    <div className="form-group">
                      <label>Date Range</label>
                      <select 
                        value={scheduleForm.dateRangeType}
                        onChange={e => setScheduleForm({...scheduleForm, dateRangeType: e.target.value})}
                      >
                        <option value="last_week">Last 7 Days</option>
                        <option value="last_month">Last 30 Days</option>
                        <option value="last_quarter">Last 90 Days</option>
                      </select>
                    </div>

                    <div className="form-group">
                      <label><FaEnvelope /> Email Recipients</label>
                      <input 
                        type="text"
                        placeholder="email1@example.com, email2@example.com"
                        value={scheduleForm.emails}
                        onChange={e => setScheduleForm({...scheduleForm, emails: e.target.value})}
                      />
                      <span className="hint">Separate multiple emails with commas</span>
                    </div>

                    <div className="modal-actions">
                      <button onClick={() => setShowScheduleModal(false)}>Cancel</button>
                      <button className="primary" onClick={handleCreateSchedule}>Create Schedule</button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReportBuilder;
